import React, { useState } from "react";
import { Card } from "react-bootstrap";
import { useHistory } from "react-router-dom";
import { AnimatedIconButton } from "components/AnimatedIconButton/AnimatedIconButton";
import IconButton from "components/IconButton/IconButton";
import { SvgComponent } from "components";
import ToolTip from "components/ToolTip/ToolTip";
import useExceptionHandler from "hooks/useExceptionHandler";
import useMessage from "hooks/useMessage";
import { useTheme } from "hooks/useTheme";
import { ArchivosManager } from "managers/ECF/archivos.manager";
import TipoSelector, { TIPO_LIST } from "./components/TipoSelector";
import PlantillaStep from "./components/PlantillaStep";
import ResultadoCarga from "./components/ResultadoCarga";

const PASO = {
  SELECCIONAR_TIPO: 1,
  PLANTILLA_CARGA: 2,
  RESULTADO: 3,
};

const PASOS_INFO = [
  { numero: PASO.SELECCIONAR_TIPO, label: "Seleccionar Tipo de e-CF" },
  { numero: PASO.PLANTILLA_CARGA, label: "Plantilla y Carga" },
  { numero: PASO.RESULTADO, label: "Resultado" },
];

const CargaMasivaECF = ({ showLoading, hideLoading }) => {
  const [pasoActual, setPasoActual] = useState(PASO.SELECCIONAR_TIPO);
  const [tipoSeleccionado, setTipoSeleccionado] = useState(null);
  const [resultado, setResultado] = useState(null);
  const [grabando, setGrabando] = useState(false);

  const history = useHistory();
  const { handleException } = useExceptionHandler();
  const { Message, Warning, Types } = useMessage();
  const { theme } = useTheme();

  const handleSeleccionarTipo = (tipoValue) => {
    setTipoSeleccionado(tipoValue);
    setResultado(null);
    setPasoActual(PASO.PLANTILLA_CARGA);
  };

  const handleVolverAtras = () => {
    if (pasoActual > PASO.SELECCIONAR_TIPO) {
      // Volver dentro del wizard limpia las selecciones previas — el usuario
      // empieza el paso anterior en blanco (sin tipo, sin archivo, sin resultado).
      setTipoSeleccionado(null);
      setResultado(null);
      setPasoActual(pasoActual - 1);
    }
  };

  const handleNuevaCarga = () => {
    setTipoSeleccionado(null);
    setResultado(null);
    setPasoActual(PASO.SELECCIONAR_TIPO);
  };

  const handleGuardar = (files) => {
    if (!files || files.length === 0) {
      Warning({ message: "Debe seleccionar un archivo .xls o .xlsx." });
      return;
    }
    setGrabando(true);
    showLoading && showLoading();

    ArchivosManager.cargarArchivo({
      TipoPlantilla: tipoSeleccionado,
      File: files,
    })
      .then((res) => {
        setResultado(res);
        setPasoActual(PASO.RESULTADO);
        if (res && res.Ok) {
          Message({ message: "Archivo cargado exitosamente." });
        } else {
          Warning({
            message:
              "El archivo no pudo ser cargado correctamente, intente de nuevo.",
          });
        }
      })
      .catch((e) => handleException(e))
      .finally(() => {
        setGrabando(false);
        hideLoading && hideLoading();
      });
  };

  const tipoConfig = tipoSeleccionado
    ? TIPO_LIST.find((t) => t.value === tipoSeleccionado)
    : null;

  return (
    <div
      style={{
        padding: "8px 16px 16px",
        overflowY: "auto",
        overflowX: "hidden",
        maxHeight: `calc(100vh - ${theme.MAX_HEIGHT_HEADER})`,
      }}
    >
      <div className="mb-4 d-flex align-items-center" style={{ gap: 12 }}>
        <IconButton
          onClick={() => history.push("/CargaMasivaECFList")}
          isDisabled={grabando}
        >
          <ToolTip message="Lista de Archivos">
            <div>
              <SvgComponent
                iconName="back"
                height="22px"
                width="22px"
                fill={theme.color1}
              />
            </div>
          </ToolTip>
        </IconButton>
        <div>
          <h4 className="mb-0">Carga Masiva de e-CF</h4>
          <small className="text-muted">
            Importa múltiples comprobantes desde un archivo Excel
          </small>
        </div>
      </div>

      <div className="d-flex align-items-center mb-4">
        {PASOS_INFO.map((paso, idx) => (
          <React.Fragment key={paso.numero}>
            {idx > 0 && (
              <div
                style={{
                  flex: 1,
                  height: 2,
                  background: pasoActual > paso.numero - 1 ? theme.color1 : "#dee2e6",
                  margin: "0 8px",
                }}
              />
            )}
            <div className="d-flex flex-column align-items-center" style={{ minWidth: 80 }}>
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 700,
                  fontSize: "0.85rem",
                  background:
                    pasoActual === paso.numero
                      ? theme.color1
                      : pasoActual > paso.numero
                        ? "#198754"
                        : "#dee2e6",
                  color: pasoActual >= paso.numero ? "#fff" : "#6c757d",
                }}
              >
                {pasoActual > paso.numero ? (
                  <i className="fas fa-check" style={{ fontSize: "0.75rem" }} />
                ) : (
                  paso.numero
                )}
              </div>
              <small
                className="mt-1 text-center"
                style={{
                  fontSize: "0.72rem",
                  color: pasoActual === paso.numero ? theme.color1 : "#6c757d",
                  fontWeight: pasoActual === paso.numero ? 600 : 400,
                }}
              >
                {paso.label}
              </small>
            </div>
          </React.Fragment>
        ))}
      </div>

      <Card>
        <Card.Header className="d-flex align-items-center justify-content-between">
          <span style={{ fontWeight: 600 }}>
            {PASOS_INFO.find((p) => p.numero === pasoActual)?.label}
            {tipoConfig && pasoActual > PASO.SELECCIONAR_TIPO && (
              <small className="text-muted ms-2" style={{ fontWeight: 400 }}>
                — {tipoConfig.label}
              </small>
            )}
          </span>
          {pasoActual > PASO.SELECCIONAR_TIPO && pasoActual < PASO.RESULTADO && (
            <AnimatedIconButton
              onClick={handleVolverAtras}
              iconName="back"
              message="Volver"
              size="38px"
              height="22px"
              width="22px"
              disabled={grabando}
            />
          )}
        </Card.Header>
        <Card.Body>
          {pasoActual === PASO.SELECCIONAR_TIPO && (
            <TipoSelector
              valor={tipoSeleccionado}
              onSeleccionar={handleSeleccionarTipo}
            />
          )}

          {pasoActual === PASO.PLANTILLA_CARGA && tipoSeleccionado && (
            <PlantillaStep
              tipo={tipoSeleccionado}
              onArchivoSeleccionado={handleGuardar}
              showLoading={showLoading}
              hideLoading={hideLoading}
              disabled={grabando}
            />
          )}

          {pasoActual === PASO.RESULTADO && resultado && (
            <ResultadoCarga
              tipo={tipoSeleccionado}
              resultado={resultado}
              onNuevaCarga={handleNuevaCarga}
            />
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default CargaMasivaECF;
